import { SettingsSurface } from "@/features/settings/components/SettingsSurface";
import { TaxExample, BarcodeExample } from "@/features/settings/components/SettingsExamples";
import { PageHeading } from "@/components/ui/page";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Percent, Scale, Plus, Edit, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { Loading } from "@/components/ui/loader";
import { useToast } from "@/hooks/use-toast";
import { paymentMethodsAPI, taxesAPI, weightSalesConfigAPI, getApiErrorPayload } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PaymentMethodIcon } from "@/components/PaymentMethodIcon";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { FieldError } from "@/components/ui/field-error";

type HubType = "methods" | "taxes" | "scale";

const TYPE_ICONS: Record<HubType, React.ElementType> = { methods: CreditCard, taxes: Percent, scale: Scale };

const TYPE_PERMISSIONS: Record<HubType, string> = {
	methods: "paymentMethods.read",
	taxes: "taxes.read",
	scale: "settings.read",
};

/** Mirrors backend `payment_methods` / `PaymentMethod` entity (GET list). */
interface PaymentMethod {
	id: number;
	code: string;
	type: string;
	bankName?: string | null;
	accountNumber?: string | null;
	accountName?: string | null;
	branch?: string | null;
	isDefault: boolean;
	description?: string | null;
	isActive: boolean;
	iconUrl?: string;
	createdAt: string;
	updatedAt?: string;
	deletedAt?: string | null;
}

const PAYMENT_METHOD_TYPES = [
	"cash",
	"card",
	"apple_pay",
	"bank_transfer",
	"check",
	"credit",
	"gift_card",
	"loyalty",
	"bnpl",
	"other",
] as const;
type PaymentMethodTypeValue = (typeof PAYMENT_METHOD_TYPES)[number];

interface PaymentMethodFormState {
	code: string;
	type: PaymentMethodTypeValue;
	bankName: string;
	accountNumber: string;
	accountName: string;
	branch: string;
	isDefault: boolean;
	description: string;
	isActive: boolean;
}

const EMPTY_METHOD_FORM: PaymentMethodFormState = {
	code: "",
	type: "cash",
	bankName: "",
	accountNumber: "",
	accountName: "",
	branch: "",
	isDefault: false,
	description: "",
	isActive: true,
};

interface Tax {
	id: string;
	name: string;
	description?: string;
	rate: number;
	code: string;
	type: string;
	isActive: boolean;
	createdAt: string;
}

interface CreateTaxData {
	name: string;
	description?: string;
	rate: number;
	code: string;
	type: string;
	isActive: boolean;
}

const EMPTY_TAX_FORM: CreateTaxData = {
	name: "",
	description: "",
	rate: 0,
	code: "",
	type: "general",
	isActive: true,
};

interface WeightSalesConfig {
	id: string;
	weightUnit: "kg" | "g" | "lb" | "oz";
	scaleStartIndicator: "stable" | "unstable" | "auto";
	decimalPrecision: number;
	printWeight: boolean;
	printPrice: boolean;
	printBoth: boolean;
	minimumWeight: number;
	maximumWeight: number;
	enableWeightPricing: boolean;
	embeddedBarcodeEnabled: boolean;
	barcodePrefixes: string;
	embeddedValueType: "weight" | "price";
	barcodeItemCodeLength: number;
	barcodeValueLength: number;
	barcodeValueDivisor: number;
	notes?: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

interface CreateWeightSalesConfigDto {
	weightUnit: "kg" | "g" | "lb" | "oz";
	scaleStartIndicator: "stable" | "unstable" | "auto";
	decimalPrecision: number;
	printWeight: boolean;
	printPrice: boolean;
	printBoth: boolean;
	minimumWeight: number;
	maximumWeight: number;
	enableWeightPricing: boolean;
	embeddedBarcodeEnabled: boolean;
	barcodePrefixes: string;
	embeddedValueType: "weight" | "price";
	barcodeItemCodeLength: number;
	barcodeValueLength: number;
	barcodeValueDivisor: number;
	notes?: string;
	isActive: boolean;
}

const EMPTY_SCALE_FORM: CreateWeightSalesConfigDto = {
	weightUnit: "kg",
	scaleStartIndicator: "stable",
	decimalPrecision: 2,
	printWeight: true,
	printPrice: true,
	printBoth: false,
	minimumWeight: 0.01,
	maximumWeight: 999.99,
	enableWeightPricing: true,
	embeddedBarcodeEnabled: true,
	barcodePrefixes: "20-29",
	embeddedValueType: "weight",
	barcodeItemCodeLength: 5,
	barcodeValueLength: 5,
	barcodeValueDivisor: 1000,
	notes: "",
	isActive: false,
};

function maskAccountNumber(value?: string | null): string {
	if (value == null || value === "") return "-";
	const trimmed = String(value).trim();
	if (trimmed.length <= 4) return trimmed;
	return `…${trimmed.slice(-4)}`;
}

function buildMethodPayload(form: PaymentMethodFormState, isUpdate: boolean): Record<string, unknown> {
	const payload: Record<string, unknown> = {
		code: form.code.trim(),
		type: form.type,
		isDefault: form.isDefault,
		isActive: form.isActive,
	};
	const description = form.description.trim();
	const bankName = form.bankName.trim();
	const accountNumber = form.accountNumber.trim();
	const accountName = form.accountName.trim();
	const branch = form.branch.trim();

	if (isUpdate) {
		payload.description = description;
		payload.bankName = bankName;
		payload.accountNumber = accountNumber;
		payload.accountName = accountName;
		payload.branch = branch;
	} else {
		if (description) payload.description = description;
		if (bankName) payload.bankName = bankName;
		if (accountNumber) payload.accountNumber = accountNumber;
		if (accountName) payload.accountName = accountName;
		if (branch) payload.branch = branch;
	}

	return payload;
}

/** Payment methods, taxes and weight-sales settings merged into one hub; type chips act as the filter. */
export default function PaymentsHubPage() {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [searchParams, setSearchParams] = useSearchParams();

	const canMethods = usePermission(TYPE_PERMISSIONS.methods);
	const canTaxes = usePermission(TYPE_PERMISSIONS.taxes);
	const canScale = usePermission(TYPE_PERMISSIONS.scale);
	const canCreateMethods = usePermission("paymentMethods.create");
	const canCreateTaxes = usePermission("taxes.create");
	const canCreateScale = usePermission("settings.read");

	const visibleTypes = useMemo<HubType[]>(() => {
		const types: HubType[] = [];
		if (canMethods) types.push("methods");
		if (canTaxes) types.push("taxes");
		if (canScale) types.push("scale");
		return types;
	}, [canMethods, canTaxes, canScale]);

	const paramType = searchParams.get("type") as HubType | null;
	const [type, setType] = useState<HubType>(
		paramType && visibleTypes.includes(paramType) ? paramType : (visibleTypes[0] ?? "methods"),
	);
	const [search, setSearch] = useState("");
	const [loadErrors, setLoadErrors] = useState({ methods: false, taxes: false, scale: false });
	const [statusFilter, setStatusFilter] = useState("all");

	const switchType = (next: HubType) => {
		setType(next);
		setSearch("");
		setStatusFilter("all");
		setSearchParams(
			prev => {
				const p = new URLSearchParams(prev);
				p.set("type", next);
				return p;
			},
			{ replace: true },
		);
	};

	// --- Payment methods ---
	const [methods, setMethods] = useState<PaymentMethod[]>([]);
	const [methodsLoading, setMethodsLoading] = useState(canMethods);
	const [methodDialogOpen, setMethodDialogOpen] = useState(false);
	const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
	const [methodForm, setMethodForm] = useState<PaymentMethodFormState>(EMPTY_METHOD_FORM);
	const [methodCodeError, setMethodCodeError] = useState<string | null>(null);
	const [methodSaving, setMethodSaving] = useState(false);
	const [showBankDetails, setShowBankDetails] = useState(false);
	const hasBankDetails = [
		methodForm.bankName,
		methodForm.accountNumber,
		methodForm.accountName,
		methodForm.branch,
	].some(value => value.trim());
	const needsBankDetails = ["bank_transfer", "check"].includes(methodForm.type) || hasBankDetails || showBankDetails;

	// --- Taxes ---
	const [taxes, setTaxes] = useState<Tax[]>([]);
	const [taxesLoading, setTaxesLoading] = useState(canTaxes);
	const [taxDialogOpen, setTaxDialogOpen] = useState(false);
	const [editingTax, setEditingTax] = useState<Tax | null>(null);
	const [taxForm, setTaxForm] = useState<CreateTaxData>(EMPTY_TAX_FORM);
	const [taxErrors, setTaxErrors] = useState<{ name?: string; rate?: string }>({});
	const [taxSaving, setTaxSaving] = useState(false);

	// --- Weight sales configs ---
	const [configs, setConfigs] = useState<WeightSalesConfig[]>([]);
	const [configsLoading, setConfigsLoading] = useState(canScale);
	const [scaleDialogOpen, setScaleDialogOpen] = useState(false);
	const [editingConfig, setEditingConfig] = useState<WeightSalesConfig | null>(null);
	const [scaleForm, setScaleForm] = useState<CreateWeightSalesConfigDto>(EMPTY_SCALE_FORM);

	const showApiError = (error: unknown, fallbackKey: string) => {
		const payload = getApiErrorPayload(error);
		const message = t(payload.key, (payload.params ?? {}) as Record<string, string>);
		toast({
			title: t("common.error"),
			description: message || t(fallbackKey),
			variant: "destructive",
		});
	};

	const fetchMethods = useCallback(async () => {
		if (!canMethods) return;
		try {
			setMethodsLoading(true);
			setLoadErrors(prev => ({ ...prev, methods: false }));
			const response = await paymentMethodsAPI.getAll();
			setMethods(response);
		} catch (error) {
			setLoadErrors(prev => ({ ...prev, methods: true }));
			showApiError(error, "common.errors.unexpected");
		} finally {
			setMethodsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canMethods]);

	const fetchTaxes = useCallback(async () => {
		if (!canTaxes) return;
		try {
			setTaxesLoading(true);
			setLoadErrors(prev => ({ ...prev, taxes: false }));
			const response = await taxesAPI.getAll();
			setTaxes(response);
		} catch {
			setLoadErrors(prev => ({ ...prev, taxes: true }));
			toast({
				title: t("common.error"),
				description: t("taxes.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setTaxesLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canTaxes]);

	const fetchConfigs = useCallback(async () => {
		if (!canScale) return;
		try {
			setConfigsLoading(true);
			setLoadErrors(prev => ({ ...prev, scale: false }));
			const data = await weightSalesConfigAPI.getAll();
			setConfigs(data);
		} catch {
			setLoadErrors(prev => ({ ...prev, scale: true }));
			toast({
				title: t("common.error"),
				description: t("weightSalesConfig.errors.fetchFailed"),
				variant: "destructive",
			});
		} finally {
			setConfigsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canScale]);

	useEffect(() => {
		fetchMethods();
	}, [fetchMethods]);
	useEffect(() => {
		fetchTaxes();
	}, [fetchTaxes]);
	useEffect(() => {
		fetchConfigs();
	}, [fetchConfigs]);

	// --- Payment method handlers ---
	const openMethodCreate = () => {
		setMethodCodeError(null);
		setShowBankDetails(false);
		setEditingMethod(null);
		setMethodForm({ ...EMPTY_METHOD_FORM });
		setMethodDialogOpen(true);
	};

	const openMethodEdit = (method: PaymentMethod) => {
		setMethodCodeError(null);
		setShowBankDetails(false);
		setEditingMethod(method);
		const methodType = PAYMENT_METHOD_TYPES.includes(method.type as PaymentMethodTypeValue)
			? (method.type as PaymentMethodTypeValue)
			: "other";
		setMethodForm({
			code: method.code,
			type: methodType,
			bankName: method.bankName ?? "",
			accountNumber: method.accountNumber ?? "",
			accountName: method.accountName ?? "",
			branch: method.branch ?? "",
			isDefault: method.isDefault,
			description: method.description ?? "",
			isActive: method.isActive,
		});
		setMethodDialogOpen(true);
	};

	const saveMethod = async () => {
		if (!methodForm.code.trim()) {
			setMethodCodeError("paymentMethods.errors.codeRequired");
			return;
		}
		if (methodSaving) return;
		try {
			setMethodSaving(true);
			if (editingMethod) {
				await paymentMethodsAPI.update(editingMethod.id, buildMethodPayload(methodForm, true));
				toast({ title: t("common.success"), description: t("paymentMethods.messages.updated") });
			} else {
				await paymentMethodsAPI.create(buildMethodPayload(methodForm, false));
				toast({ title: t("common.success"), description: t("paymentMethods.messages.created") });
			}
			setMethodDialogOpen(false);
			setEditingMethod(null);
			setMethodForm({ ...EMPTY_METHOD_FORM });
			fetchMethods();
		} catch (error) {
			showApiError(error, "common.errors.unexpected");
		} finally {
			setMethodSaving(false);
		}
	};

	const deleteMethod = async (id: number) => {
		if (!(await confirmAction(t("paymentMethods.confirmations.delete"), { confirmLabel: t("common.delete") })))
			return;
		try {
			await paymentMethodsAPI.remove(id);
			toast({ title: t("common.success"), description: t("paymentMethods.messages.deleted") });
			fetchMethods();
		} catch (error) {
			showApiError(error, "common.errors.unexpected");
		}
	};

	// --- Tax handlers ---
	const openTaxCreate = () => {
		setTaxErrors({});
		setEditingTax(null);
		setTaxForm({ ...EMPTY_TAX_FORM });
		setTaxDialogOpen(true);
	};

	const openTaxEdit = (tax: Tax) => {
		setTaxErrors({});
		setEditingTax(tax);
		setTaxForm({
			name: tax.name,
			description: tax.description || "",
			rate: tax.rate,
			code: tax.code,
			type: tax.type,
			isActive: tax.isActive,
		});
		setTaxDialogOpen(true);
	};

	const saveTax = async () => {
		const errors: { name?: string; rate?: string } = {};
		if (!taxForm.name.trim()) errors.name = "validation.required";
		if (taxForm.rate === undefined || taxForm.rate === null || Number.isNaN(taxForm.rate)) {
			errors.rate = "validation.required";
		} else if (taxForm.rate < 0) {
			errors.rate = "validation.notNegative";
		} else if (taxForm.rate > 100) {
			errors.rate = "validation.percentMax";
		}
		setTaxErrors(errors);
		if (Object.keys(errors).length > 0 || taxSaving) return;
		try {
			setTaxSaving(true);
			if (editingTax) {
				await taxesAPI.update(editingTax.id, taxForm);
				toast({ title: t("common.success"), description: t("taxes.messages.updated") });
			} else {
				await taxesAPI.create(taxForm);
				toast({ title: t("common.success"), description: t("taxes.messages.created") });
			}
			setTaxDialogOpen(false);
			setEditingTax(null);
			setTaxForm({ ...EMPTY_TAX_FORM });
			fetchTaxes();
		} catch {
			toast({
				title: t("common.error"),
				description: t(editingTax ? "taxes.errors.updateFailed" : "taxes.errors.createFailed"),
				variant: "destructive",
			});
		} finally {
			setTaxSaving(false);
		}
	};

	const deleteTax = async (id: number | string) => {
		if (!(await confirmAction(t("taxes.confirmations.delete"), { confirmLabel: t("common.delete") }))) return;
		try {
			await taxesAPI.remove(id);
			toast({ title: t("common.success"), description: t("taxes.messages.deleted") });
			fetchTaxes();
		} catch {
			toast({
				title: t("common.error"),
				description: t("taxes.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	// --- Weight sales config handlers ---
	const openScaleCreate = () => {
		setEditingConfig(null);
		setScaleForm({ ...EMPTY_SCALE_FORM });
		setScaleDialogOpen(true);
	};

	const openScaleEdit = (config: WeightSalesConfig) => {
		setEditingConfig(config);
		setScaleForm({
			weightUnit: config.weightUnit,
			// Stored compatibility fields: not consumed by barcode-label sales or receipt rendering.
			scaleStartIndicator: config.scaleStartIndicator,
			decimalPrecision: config.decimalPrecision,
			printWeight: config.printWeight,
			printPrice: config.printPrice,
			printBoth: config.printBoth,
			minimumWeight: config.minimumWeight,
			maximumWeight: config.maximumWeight,
			enableWeightPricing: config.enableWeightPricing,
			embeddedBarcodeEnabled: config.embeddedBarcodeEnabled ?? true,
			barcodePrefixes: config.barcodePrefixes ?? "20-29",
			embeddedValueType: config.embeddedValueType ?? "weight",
			barcodeItemCodeLength: config.barcodeItemCodeLength ?? 5,
			barcodeValueLength: config.barcodeValueLength ?? 5,
			barcodeValueDivisor: config.barcodeValueDivisor ?? 1000,
			notes: config.notes || "",
			isActive: config.isActive,
		});
		setScaleDialogOpen(true);
	};

	const saveScale = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			if (editingConfig) {
				await weightSalesConfigAPI.update(editingConfig.id, scaleForm);
				toast({ title: t("common.success"), description: t("weightSalesConfig.messages.updated") });
			} else {
				await weightSalesConfigAPI.create(scaleForm);
				toast({ title: t("common.success"), description: t("weightSalesConfig.messages.created") });
			}
			setScaleDialogOpen(false);
			setEditingConfig(null);
			setScaleForm({ ...EMPTY_SCALE_FORM });
			fetchConfigs();
		} catch (error) {
			showApiError(error, "common.errors.unexpected");
		}
	};

	const deleteScale = async (id: number | string) => {
		if (!(await confirmAction(t("weightSalesConfig.confirmations.delete"), { confirmLabel: t("common.delete") })))
			return;
		try {
			await weightSalesConfigAPI.delete(id);
			toast({ title: t("common.success"), description: t("weightSalesConfig.messages.deleted") });
			fetchConfigs();
		} catch (error) {
			showApiError(error, "common.errors.unexpected");
		}
	};

	const activateScale = async (id: number | string) => {
		try {
			await weightSalesConfigAPI.activate(id);
			toast({ title: t("common.success"), description: t("weightSalesConfig.messages.activated") });
			fetchConfigs();
		} catch (error) {
			showApiError(error, "common.errors.unexpected");
		}
	};

	const deactivateScale = async (id: number | string) => {
		try {
			await weightSalesConfigAPI.deactivate(id);
			toast({ title: t("common.success"), description: t("weightSalesConfig.messages.deactivated") });
			fetchConfigs();
		} catch (error) {
			showApiError(error, "common.errors.unexpected");
		}
	};

	// --- Helpers ---
	const methodTypeLabel = (methodType: string) =>
		t(`paymentMethods.types.${methodType}`, { defaultValue: methodType });

	const bankSummary = (method: PaymentMethod) => {
		if (method.type === "cash" && !method.bankName && !method.accountNumber) {
			return "-";
		}
		const parts = [
			method.bankName,
			method.accountName,
			method.accountNumber ? maskAccountNumber(method.accountNumber) : null,
		]
			.filter(Boolean)
			.join(" · ");
		return parts || "-";
	};

	const getWeightUnitLabel = (unit: string) => {
		const units = {
			kg: t("weightSalesConfig.units.kilogram"),
			g: t("weightSalesConfig.units.gram"),
			lb: t("weightSalesConfig.units.pound"),
			oz: t("weightSalesConfig.units.ounce"),
		};
		return units[unit as keyof typeof units] || unit;
	};

	const filteredMethods = useMemo(() => {
		const q = search.trim().toLowerCase();
		return methods.filter(m => {
			if (q && !m.code.toLowerCase().includes(q) && !(m.description ?? "").toLowerCase().includes(q))
				return false;
			if (statusFilter === "active" && !m.isActive) return false;
			if (statusFilter === "inactive" && m.isActive) return false;
			return true;
		});
	}, [methods, search, statusFilter]);

	const filteredTaxes = useMemo(() => {
		const q = search.trim().toLowerCase();
		return taxes.filter(tax => {
			if (q && !tax.name.toLowerCase().includes(q) && !tax.code.toLowerCase().includes(q)) return false;
			if (statusFilter === "active" && !tax.isActive) return false;
			if (statusFilter === "inactive" && tax.isActive) return false;
			return true;
		});
	}, [taxes, search, statusFilter]);

	const isFiltering = search.trim() !== "" || statusFilter !== "all";

	const filteredConfigs = useMemo(() => {
		return configs.filter(config => {
			if (statusFilter === "active" && !config.isActive) return false;
			if (statusFilter === "inactive" && config.isActive) return false;
			return true;
		});
	}, [configs, statusFilter]);

	const activeMethodsCount = useMemo(() => methods.filter(m => m.isActive).length, [methods]);
	const activeTaxesCount = useMemo(() => taxes.filter(tax => tax.isActive).length, [taxes]);

	const typeLabels: Record<HubType, string> = {
		methods: t("navigation.paymentMethods"),
		taxes: t("navigation.taxes"),
		scale: t("settingsDesign.scaleSetup"),
	};

	const contentLoading =
		(type === "methods" && methodsLoading) ||
		(type === "taxes" && taxesLoading) ||
		(type === "scale" && configsLoading);

	return (
		<div className="settings-workspace space-y-5 p-4 md:p-6">
			<div>
				<PageHeading className="mb-1 ">{t("navigation.paymentsAndTaxes")}</PageHeading>
				<p className="text-muted-foreground text-sm">{t("paymentsHub.description")}</p>
			</div>

			{/* Unified toolbar: type chips + contextual filters + contextual action */}
			<div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2">
				{visibleTypes.map(key => {
					const Icon = TYPE_ICONS[key];
					const isSelected = type === key;
					return (
						<button
							key={key}
							type="button"
							onClick={() => switchType(key)}
							aria-pressed={isSelected}
							className={cn(
								"flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
								isSelected ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted/60",
							)}
						>
							<Icon className="h-4 w-4" />
							{typeLabels[key]}
							<span className="text-xs tabular-nums">
								{key === "methods"
									? activeMethodsCount
									: key === "taxes"
										? activeTaxesCount
										: configs.length}
							</span>
						</button>
					);
				})}

				<div className="ms-auto flex flex-wrap items-center gap-2">
					{type !== "scale" && (
						<div className="relative w-full sm:w-auto">
							<Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={search}
								onChange={e => setSearch(e.target.value)}
								aria-label={
									type === "methods" ? t("paymentsHub.searchMethods") : t("paymentsHub.searchTaxes")
								}
								placeholder={
									type === "methods" ? t("paymentsHub.searchMethods") : t("paymentsHub.searchTaxes")
								}
								className="h-9 w-full sm:w-48 ps-8"
							/>
						</div>
					)}
					<Select value={statusFilter} onValueChange={setStatusFilter}>
						<SelectTrigger aria-label={t("common.status")} className="h-11 w-32">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t("common.all")}</SelectItem>
							<SelectItem value="active">{t("common.active")}</SelectItem>
							<SelectItem value="inactive">{t("common.inactive")}</SelectItem>
						</SelectContent>
					</Select>
					{type === "methods" && (
						<PermissionGate permission="paymentMethods.create">
							<Button size="sm" className="h-9" onClick={openMethodCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("paymentMethods.actions.addMethod")}
							</Button>
						</PermissionGate>
					)}
					{type === "taxes" && (
						<PermissionGate permission="taxes.create">
							<Button size="sm" className="h-9" onClick={openTaxCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("taxes.actions.addTax")}
							</Button>
						</PermissionGate>
					)}
					{type === "scale" && (
						<PermissionGate permission="settings.read">
							<Button size="sm" className="h-9" onClick={openScaleCreate}>
								<Plus className="h-4 w-4 me-1" />
								{t("weightSalesConfig.actions.create")}
							</Button>
						</PermissionGate>
					)}
				</div>
			</div>

			{type !== "methods" && (
				<div>
					<p className="mt-1 text-sm text-muted-foreground">
						{t(type === "taxes" ? "settingsDesign.taxHint" : "settingsDesign.scaleHint")}
					</p>
				</div>
			)}
			{loadErrors[type] && (
				<div role="alert" className="settings-surface flex flex-wrap items-center justify-between gap-3">
					<p className="text-sm">
						{t(
							type === "taxes"
								? "taxes.errors.fetchFailed"
								: type === "scale"
									? "weightSalesConfig.errors.fetchFailed"
									: "common.errors.unexpected",
						)}
					</p>
					<Button
						variant="outline"
						onClick={type === "taxes" ? fetchTaxes : type === "scale" ? fetchConfigs : fetchMethods}
					>
						{t("common.retry")}
					</Button>
				</div>
			)}
			{contentLoading ? (
				<Loading />
			) : (
				<>
					{/* Payment methods */}
					{type === "methods" &&
						(filteredMethods.length === 0 ? (
							loadErrors.methods ? null : (
								<div className="rounded-xl border">
									{isFiltering ? (
										<EmptyState icon={Search} title={t("common.noResults")} />
									) : (
										<EmptyState
											icon={CreditCard}
											title={t("paymentMethods.empty.title")}
											action={
												canCreateMethods
													? {
															label: t("paymentMethods.actions.addMethod"),
															onClick: openMethodCreate,
														}
													: undefined
											}
										/>
									)}
								</div>
							)
						) : (
							<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
								{filteredMethods.map(method => (
									<SettingsSurface key={method.id} className="flex flex-col gap-4">
										<div className="flex items-start gap-3">
											{method.iconUrl ? (
												<PaymentMethodIcon
													iconUrl={method.iconUrl}
													alt=""
													className="h-10 w-10 object-contain"
												/>
											) : (
												<div className="rounded-lg bg-primary/10 p-3 text-primary">
													<CreditCard className="h-5 w-5" />
												</div>
											)}
											<div className="min-w-0 flex-1">
												<h3 className="text-base font-semibold">
													{method.description || method.code}
												</h3>
												<p className="mt-1 text-sm text-muted-foreground">
													{methodTypeLabel(method.type)} · <bdi>{method.code}</bdi>
												</p>
											</div>
											<Badge variant={method.isActive ? "success" : "secondary"}>
												{t(method.isActive ? "common.active" : "common.inactive")}
											</Badge>
										</div>
										<div className="flex flex-wrap items-center gap-2 text-sm">
											{method.isDefault && (
												<Badge variant="secondary">{t("paymentTaxDesign.defaultMethod")}</Badge>
											)}
											{bankSummary(method) !== "-" && (
												<p className="text-muted-foreground">
													<bdi>{bankSummary(method)}</bdi>
												</p>
											)}
										</div>
										<div className="mt-auto flex justify-end gap-2 border-t pt-4">
											<PermissionGate permission="paymentMethods.update">
												<Button variant="outline" onClick={() => openMethodEdit(method)}>
													<Edit className="me-2 h-4 w-4" />
													{t("common.edit")}
												</Button>
											</PermissionGate>
											<PermissionGate permission="paymentMethods.delete">
												<Button
													variant="ghost"
													size="icon"
													aria-label={t("common.delete")}
													onClick={() => deleteMethod(method.id)}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</PermissionGate>
										</div>
									</SettingsSurface>
								))}
							</div>
						))}

					{/* Taxes */}
					{type === "taxes" &&
						(filteredTaxes.length === 0 ? (
							loadErrors.taxes ? null : (
								<div className="rounded-xl border">
									{isFiltering ? (
										<EmptyState icon={Search} title={t("common.noResults")} />
									) : (
										<EmptyState
											icon={Percent}
											title={t("taxes.empty.title")}
											action={
												canCreateTaxes
													? { label: t("taxes.actions.addTax"), onClick: openTaxCreate }
													: undefined
											}
										/>
									)}
								</div>
							)
						) : (
							<div className="grid gap-4 xl:grid-cols-2">
								{filteredTaxes.map(tax => (
									<SettingsSurface key={tax.id} className="space-y-5">
										<div className="flex items-start justify-between gap-4">
											<div className="min-w-0">
												<div className="flex flex-wrap items-center gap-2">
													<h3 className="text-base font-semibold">{tax.name}</h3>
													<Badge variant={tax.isActive ? "success" : "secondary"}>
														{tax.isActive ? t("common.active") : t("common.inactive")}
													</Badge>
												</div>
												<p className="mt-1 text-sm text-muted-foreground">{tax.description}</p>
											</div>
											<strong className="shrink-0 text-2xl tabular-nums">
												<bdi>{tax.rate}%</bdi>
											</strong>
										</div>

										<TaxExample rate={Number(tax.rate)} />
										<div className="flex justify-end gap-2">
											<PermissionGate permission="taxes.update">
												<Button variant="outline" onClick={() => openTaxEdit(tax)}>
													<Edit className="me-2 h-4 w-4" />
													{t("common.edit")}
												</Button>
											</PermissionGate>
											<PermissionGate permission="taxes.delete">
												<Button
													variant="ghost"
													size="icon"
													aria-label={t("common.delete")}
													onClick={() => deleteTax(tax.id)}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</PermissionGate>
										</div>
									</SettingsSurface>
								))}
							</div>
						))}

					{/* Weight sales configs */}
					{type === "scale" &&
						(filteredConfigs.length === 0 ? (
							loadErrors.scale ? null : (
								<div className="rounded-xl border">
									{statusFilter !== "all" ? (
										<EmptyState icon={Search} title={t("common.noResults")} />
									) : (
										<EmptyState
											icon={Scale}
											title={t("weightSalesConfig.empty.title")}
											action={
												canCreateScale
													? {
															label: t("weightSalesConfig.actions.create"),
															onClick: openScaleCreate,
														}
													: undefined
											}
										/>
									)}
								</div>
							)
						) : (
							<div className="space-y-4">
								{filteredConfigs.map(config => (
									<SettingsSurface key={config.id}>
										<div className="flex flex-wrap items-start justify-between gap-3">
											<div>
												<h3 className="flex items-center gap-2 text-base font-semibold">
													<Scale className="h-5 w-5 text-primary" />
													{t("settingsDesign.barcodeMode")}
												</h3>
												{config.notes && (
													<p className="mt-1 text-sm text-muted-foreground">{config.notes}</p>
												)}
											</div>
											<Badge variant={config.isActive ? "success" : "secondary"}>
												{config.isActive ? t("common.active") : t("common.inactive")}
											</Badge>
										</div>
										<div className="mt-5 grid gap-6">
											<dl className="settings-definition-grid">
												{[
													[
														t("weightSalesConfig.table.headers.weightUnit"),
														getWeightUnitLabel(config.weightUnit),
													],
													[
														t("weightSalesConfig.table.headers.precision"),
														String(config.decimalPrecision),
													],
													[
														t("weightSalesConfig.table.headers.weightRange"),
														`${config.minimumWeight} – ${config.maximumWeight}`,
													],
													[
														t("weightSalesConfig.form.enableWeightPricing"),
														t(
															config.enableWeightPricing
																? "common.active"
																: "common.inactive",
														),
													],
												].map(([label, value]) => (
													<div key={label}>
														<dt>{label}</dt>
														<dd>{value}</dd>
													</div>
												))}
											</dl>
											{config.embeddedBarcodeEnabled && <BarcodeExample config={config} />}
										</div>
										<div className="mt-5 flex flex-wrap justify-end gap-2 border-t pt-4">
											<PermissionGate permission="settings.read">
												<Button variant="outline" onClick={() => openScaleEdit(config)}>
													<Edit className="me-2 h-4 w-4" />
													{t("common.edit")}
												</Button>
												<Button
													variant="outline"
													onClick={() =>
														config.isActive
															? deactivateScale(config.id)
															: activateScale(config.id)
													}
												>
													{config.isActive
														? t("settingsDesign.deactivate")
														: t("settingsDesign.activate")}
												</Button>
												<Button
													variant="ghost"
													size="icon"
													aria-label={t("common.delete")}
													onClick={() => deleteScale(config.id)}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</PermissionGate>
										</div>
									</SettingsSurface>
								))}
							</div>
						))}
				</>
			)}

			{/* Payment method dialog */}
			<Dialog
				open={methodDialogOpen}
				onOpenChange={open => {
					setMethodDialogOpen(open);
					if (!open) {
						setEditingMethod(null);
						setMethodForm({ ...EMPTY_METHOD_FORM });
					}
				}}
			>
				<DialogContent className="settings-form max-w-2xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingMethod
								? t("paymentMethods.actions.editMethod")
								: t("paymentMethods.actions.addMethod")}
						</DialogTitle>
						<DialogDescription>
							{editingMethod
								? t("paymentMethods.dialogs.editDescription")
								: t("paymentMethods.dialogs.createDescription")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-5">
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="space-y-1">
								<Label htmlFor="description">{t("paymentTaxDesign.checkoutName")}</Label>
								<Input
									id="description"
									value={methodForm.description}
									onChange={e =>
										setMethodForm({
											...methodForm,
											description: e.target.value,
										})
									}
									placeholder={t("paymentTaxDesign.checkoutNamePlaceholder")}
								/>
							</div>
							<div className="space-y-1">
								<Label>{t("paymentMethods.fields.type")}</Label>
								<Select
									value={methodForm.type}
									onValueChange={v =>
										setMethodForm({
											...methodForm,
											type: v as PaymentMethodTypeValue,
										})
									}
								>
									<SelectTrigger aria-label={t("paymentMethods.fields.type")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{PAYMENT_METHOD_TYPES.map(tKey => (
											<SelectItem key={tKey} value={tKey}>
												{methodTypeLabel(tKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						{editingMethod ? (
							<p data-testid="payment-method-code" className="text-xs text-muted-foreground">
								{t("paymentMethods.fields.code")}: <bdi>{methodForm.code}</bdi>
							</p>
						) : (
							<div className="space-y-1">
								<Label htmlFor="code">
									{t("paymentMethods.fields.code")} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="code"
									value={methodForm.code}
									onChange={e => {
										setMethodForm({ ...methodForm, code: e.target.value });
										setMethodCodeError(null);
									}}
									placeholder={t("paymentMethods.placeholders.code")}
									disabled={!!editingMethod}
									autoComplete="off"
									aria-invalid={!!methodCodeError}
								/>
								<FieldError message={methodCodeError} />
								<p className="text-xs text-muted-foreground">{t("paymentMethods.form.codeHint")}</p>
							</div>
						)}
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="setting-toggle">
								<Label htmlFor="isActive">{t("paymentMethods.fields.isActive")}</Label>
								<Switch
									id="isActive"
									checked={methodForm.isActive}
									onCheckedChange={checked => setMethodForm({ ...methodForm, isActive: checked })}
								/>
							</div>
							<div className="setting-toggle">
								<Label htmlFor="isDefault">{t("paymentTaxDesign.defaultMethodLabel")}</Label>
								<Switch
									id="isDefault"
									checked={methodForm.isDefault}
									onCheckedChange={checked => setMethodForm({ ...methodForm, isDefault: checked })}
								/>
							</div>
						</div>
						{needsBankDetails ? (
							<section className="space-y-4 border-t pt-4" aria-labelledby="bank-details-title">
								<h3 id="bank-details-title" className="font-semibold text-sm">
									{t("paymentTaxDesign.bankDetails")}
								</h3>
								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-1">
										<Label htmlFor="bankName">{t("paymentMethods.fields.bankName")}</Label>
										<Input
											id="bankName"
											value={methodForm.bankName}
											onChange={e => setMethodForm({ ...methodForm, bankName: e.target.value })}
											placeholder={t("paymentMethods.placeholders.bankName")}
										/>
									</div>
									<div className="space-y-1">
										<Label htmlFor="branch">{t("paymentTaxDesign.bankBranch")}</Label>
										<Input
											id="branch"
											value={methodForm.branch}
											onChange={e => setMethodForm({ ...methodForm, branch: e.target.value })}
											placeholder={t("paymentMethods.placeholders.branch")}
										/>
									</div>
									<div className="space-y-1">
										<Label htmlFor="accountName">{t("paymentMethods.fields.accountName")}</Label>
										<Input
											id="accountName"
											value={methodForm.accountName}
											onChange={e =>
												setMethodForm({ ...methodForm, accountName: e.target.value })
											}
											placeholder={t("paymentMethods.placeholders.accountName")}
										/>
									</div>
									<div className="space-y-1">
										<Label htmlFor="accountNumber">
											{t("paymentMethods.fields.accountNumber")}
										</Label>
										<Input
											id="accountNumber"
											value={methodForm.accountNumber}
											onChange={e =>
												setMethodForm({ ...methodForm, accountNumber: e.target.value })
											}
											placeholder={t("paymentMethods.placeholders.accountNumber")}
										/>
									</div>
								</div>
							</section>
						) : (
							<Button type="button" variant="ghost" onClick={() => setShowBankDetails(true)}>
								{t("paymentTaxDesign.addBankDetails")}
							</Button>
						)}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setMethodDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={saveMethod} disabled={methodSaving}>
							{methodSaving
								? t("common.saving")
								: editingMethod
									? t("common.update")
									: t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Tax dialog */}
			<Dialog
				open={taxDialogOpen}
				onOpenChange={open => {
					setTaxDialogOpen(open);
					if (!open) {
						setEditingTax(null);
						setTaxForm({ ...EMPTY_TAX_FORM });
					}
				}}
			>
				<DialogContent className="settings-form max-w-3xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{editingTax ? t("taxes.actions.editTax") : t("taxes.actions.addTax")}</DialogTitle>
						<DialogDescription>
							{editingTax ? t("taxes.dialogs.editDescription") : t("taxes.dialogs.createDescription")}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-5">
						<div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
							<div>
								<Label htmlFor="tax-name">
									{t("taxes.fields.name")} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="tax-name"
									value={taxForm.name}
									onChange={e => {
										setTaxForm({ ...taxForm, name: e.target.value });
										setTaxErrors(prev => ({ ...prev, name: undefined }));
									}}
									placeholder={t("taxes.placeholders.name")}
									aria-invalid={!!taxErrors.name}
								/>
								<FieldError message={taxErrors.name} />
							</div>
							<div>
								<Label htmlFor="tax-rate">
									{t("paymentTaxDesign.rateLabel")} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="tax-rate"
									type="number"
									step="0.01"
									min="0"
									max="100"
									value={Number.isNaN(taxForm.rate) ? "" : taxForm.rate}
									onChange={e => {
										setTaxForm({ ...taxForm, rate: parseFloat(e.target.value) });
										setTaxErrors(prev => ({ ...prev, rate: undefined }));
									}}
									placeholder="0.00"
									aria-invalid={!!taxErrors.rate}
								/>
								<FieldError message={taxErrors.rate} />
							</div>
						</div>
						<TaxExample rate={taxForm.rate} />
						<div className="setting-toggle">
							<Label htmlFor="tax-isActive">{t("taxes.fields.isActive")}</Label>
							<Switch
								id="tax-isActive"
								checked={taxForm.isActive}
								onCheckedChange={checked => setTaxForm({ ...taxForm, isActive: checked })}
							/>
						</div>
						<p className="text-xs text-muted-foreground">{t("paymentTaxDesign.taxAvailability")}</p>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setTaxDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={saveTax} disabled={taxSaving}>
							{taxSaving ? t("common.saving") : editingTax ? t("common.update") : t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Weight sales config dialog */}
			<Dialog open={scaleDialogOpen} onOpenChange={setScaleDialogOpen}>
				<DialogContent className="settings-form scale-dialog max-w-5xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editingConfig
								? t("weightSalesConfig.actions.edit")
								: t("weightSalesConfig.actions.create")}
						</DialogTitle>
						<DialogDescription>{t("weightSalesConfig.form.description")}</DialogDescription>
					</DialogHeader>

					<form onSubmit={saveScale} className="space-y-6">
						<div className="scale-method">
							<div>
								<h3 className="font-semibold">{t("settingsDesign.barcodeMode")}</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									{t("productEditor.barcodeModeHint")}
								</p>
							</div>
							<Switch
								id="embeddedBarcodeEnabled"
								aria-label={t("settingsDesign.barcodeMode")}
								checked={scaleForm.embeddedBarcodeEnabled}
								onCheckedChange={checked =>
									setScaleForm({ ...scaleForm, embeddedBarcodeEnabled: checked })
								}
							/>
						</div>
						{scaleForm.embeddedBarcodeEnabled && (
							<>
								<div className="scale-primary-fields">
									<div className="space-y-2">
										<Label htmlFor="barcodePrefixes">
											{t("weightSalesConfig.barcode.prefixes")}
										</Label>
										<Input
											id="barcodePrefixes"
											value={scaleForm.barcodePrefixes}
											onChange={e =>
												setScaleForm({
													...scaleForm,
													barcodePrefixes: e.target.value,
												})
											}
											placeholder="20-29"
										/>
										<p className="text-muted-foreground text-xs">
											{t("weightSalesConfig.barcode.prefixesHelp")}
										</p>
									</div>
									<div className="space-y-2">
										<Label htmlFor="barcodeItemCodeLength">
											{t("weightSalesConfig.barcode.itemCodeLength")}
										</Label>
										<Input
											id="barcodeItemCodeLength"
											type="number"
											min="1"
											max="10"
											value={scaleForm.barcodeItemCodeLength}
											onChange={e =>
												setScaleForm({
													...scaleForm,
													barcodeItemCodeLength: parseInt(e.target.value) || 5,
												})
											}
										/>
									</div>
									<div role="group" aria-labelledby="scale-value-label" className="min-w-0 space-y-2">
										<div id="scale-value-label" className="text-sm font-medium">
											{t("weightSalesConfig.barcode.valueType")}
										</div>
										<div className="scale-value-toggle">
											{(["weight", "price"] as const).map(value => (
												<button
													type="button"
													key={value}
													aria-pressed={scaleForm.embeddedValueType === value}
													onClick={() =>
														setScaleForm({ ...scaleForm, embeddedValueType: value })
													}
												>
													{t(
														value === "weight"
															? "weightSalesConfig.barcode.valueWeight"
															: "weightSalesConfig.barcode.valuePrice",
													)}
												</button>
											))}
										</div>
									</div>
									<div className="space-y-2">
										<Label htmlFor="weightUnit">{t("weightSalesConfig.form.weightUnit")}</Label>
										<Select
											value={scaleForm.weightUnit}
											onValueChange={(value: "kg" | "g" | "lb" | "oz") =>
												setScaleForm({ ...scaleForm, weightUnit: value })
											}
										>
											<SelectTrigger aria-label={t("weightSalesConfig.form.weightUnit")}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="kg">
													{t("weightSalesConfig.units.kilogram")}
												</SelectItem>
												<SelectItem value="g">{t("weightSalesConfig.units.gram")}</SelectItem>
												<SelectItem value="lb">{t("weightSalesConfig.units.pound")}</SelectItem>
												<SelectItem value="oz">{t("weightSalesConfig.units.ounce")}</SelectItem>
											</SelectContent>
										</Select>
									</div>
								</div>
								<BarcodeExample config={scaleForm} />
								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="barcodeValueLength">
											{t("weightSalesConfig.barcode.valueLength")}
										</Label>
										<Input
											id="barcodeValueLength"
											type="number"
											min="1"
											max="10"
											value={scaleForm.barcodeValueLength}
											onChange={e =>
												setScaleForm({
													...scaleForm,
													barcodeValueLength: parseInt(e.target.value) || 5,
												})
											}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="barcodeValueDivisor">
											{t("weightSalesConfig.barcode.valueDivisor")}
										</Label>
										<Input
											id="barcodeValueDivisor"
											type="number"
											min="1"
											value={scaleForm.barcodeValueDivisor}
											onChange={e =>
												setScaleForm({
													...scaleForm,
													barcodeValueDivisor: parseInt(e.target.value) || 1000,
												})
											}
										/>
									</div>
								</div>
								<p className="text-xs text-muted-foreground">
									{t("weightSalesConfig.barcode.structureHelp")}
								</p>
							</>
						)}
						{!scaleForm.embeddedBarcodeEnabled && (
							<div className="max-w-sm">
								<div className="space-y-2">
									<Label htmlFor="weightUnit">{t("weightSalesConfig.form.weightUnit")}</Label>
									<Select
										value={scaleForm.weightUnit}
										onValueChange={(value: "kg" | "g" | "lb" | "oz") =>
											setScaleForm({ ...scaleForm, weightUnit: value })
										}
									>
										<SelectTrigger aria-label={t("weightSalesConfig.form.weightUnit")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="kg">{t("weightSalesConfig.units.kilogram")}</SelectItem>
											<SelectItem value="g">{t("weightSalesConfig.units.gram")}</SelectItem>
											<SelectItem value="lb">{t("weightSalesConfig.units.pound")}</SelectItem>
											<SelectItem value="oz">{t("weightSalesConfig.units.ounce")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
						)}
						<details className="scale-additional" open>
							<summary className="min-h-11 cursor-pointer font-semibold text-sm">
								{t("productEditor.advancedScale")}
							</summary>
							<div className="space-y-4">
								<div className="setting-toggle">
									<Label htmlFor="enableWeightPricing">
										{t("weightSalesConfig.form.enableWeightPricing")}
									</Label>
									<Switch
										id="enableWeightPricing"
										checked={scaleForm.enableWeightPricing}
										onCheckedChange={checked =>
											setScaleForm({ ...scaleForm, enableWeightPricing: checked })
										}
									/>
								</div>
								<div className="grid gap-4 sm:grid-cols-3">
									<div className="space-y-2">
										<Label htmlFor="decimalPrecision">
											{t("weightSalesConfig.form.decimalPrecision")}
										</Label>
										<Select
											value={scaleForm.decimalPrecision.toString()}
											onValueChange={value =>
												setScaleForm({
													...scaleForm,
													decimalPrecision: parseInt(value),
												})
											}
										>
											<SelectTrigger aria-label={t("weightSalesConfig.form.decimalPrecision")}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="0">0</SelectItem>
												<SelectItem value="1">1</SelectItem>
												<SelectItem value="2">2</SelectItem>
												<SelectItem value="3">3</SelectItem>
												<SelectItem value="4">4</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="minimumWeight">
											{t("weightSalesConfig.form.minimumWeight")}
										</Label>
										<Input
											id="minimumWeight"
											type="number"
											step="0.01"
											min="0"
											value={scaleForm.minimumWeight}
											onChange={e =>
												setScaleForm({
													...scaleForm,
													minimumWeight: parseFloat(e.target.value) || 0,
												})
											}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="maximumWeight">
											{t("weightSalesConfig.form.maximumWeight")}
										</Label>
										<Input
											id="maximumWeight"
											type="number"
											step="0.01"
											min="0"
											value={scaleForm.maximumWeight}
											onChange={e =>
												setScaleForm({
													...scaleForm,
													maximumWeight: parseFloat(e.target.value) || 0,
												})
											}
										/>
									</div>
								</div>
								<div className="space-y-2">
									<Label htmlFor="notes">{t("weightSalesConfig.form.notes")}</Label>
									<Input
										id="notes"
										value={scaleForm.notes}
										onChange={e => setScaleForm({ ...scaleForm, notes: e.target.value })}
										placeholder={t("weightSalesConfig.form.notesPlaceholder")}
									/>
								</div>
							</div>
						</details>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setScaleDialogOpen(false);
									setEditingConfig(null);
									setScaleForm({ ...EMPTY_SCALE_FORM });
								}}
							>
								{t("common.cancel")}
							</Button>
							<Button type="submit">{editingConfig ? t("common.update") : t("common.create")}</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
